// Small shared UI primitives for the pickup app.
// IceOps brand: LIGHT theme - white cards on a light slate page, dark text,
// ICE BLUE primary accent (matches the main iceops.app content surface).
// Semantic status colors (green=IN, amber=maybe, rose=out/cancel) are allowed.
// All form fields use text-base (>=16px) to avoid iOS zoom.

export function Spinner({ label = 'Loading' }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-slate-500">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-ice-500" />
      <p className="mt-4 text-base">{label}...</p>
    </div>
  )
}

export function ErrorNote({ message }) {
  if (!message) return null
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-base text-rose-700">
      {message}
    </div>
  )
}

export function SuccessNote({ children }) {
  return (
    <div className="rounded-lg border border-ice-200 bg-ice-50 px-4 py-3 text-base text-ice-800">
      {children}
    </div>
  )
}

// White ESPN-style card on the light page.
export function Card({ children, className = '' }) {
  return (
    <div
      className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-card ${className}`}
    >
      {children}
    </div>
  )
}

export function Badge({ children, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700 border border-slate-200',
    ice: 'bg-ice-50 text-ice-700 border border-ice-200',
    green: 'bg-green-100 text-green-700 border border-green-200',
    rose: 'bg-rose-100 text-rose-700 border border-rose-200',
    amber: 'bg-amber-100 text-amber-800 border border-amber-200',
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${
        tones[tone] || tones.slate
      }`}
    >
      {children}
    </span>
  )
}

// Big bold status pill for the scoreboard skate state. Solid fills read well
// on both the white cards and the dark scoreboard header strip.
export function StatusPill({ status }) {
  const map = {
    on: 'bg-green-500 text-white',
    cancelled: 'bg-rose-500 text-white',
    none: 'bg-slate-200 text-slate-700',
  }
  const label = {
    on: 'SKATE ON',
    cancelled: 'CANCELLED',
    none: 'NO SKATE YET',
  }
  const key = status === 'on' ? 'on' : status === 'cancelled' ? 'cancelled' : 'none'
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-extrabold uppercase tracking-wide shadow-sm ${map[key]}`}
    >
      {key === 'on' && <PuckIcon className="h-4 w-4" />}
      {label[key]}
    </span>
  )
}

// Visual jersey chip - Light = pale chip with dark text, Dark = navy chip with light text.
export function JerseyChip({ jersey, children }) {
  const j = (jersey || 'either').toLowerCase()
  if (j === 'light') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-2.5 py-0.5 text-sm font-medium text-slate-900">
        <JerseyIcon className="h-3.5 w-3.5 text-slate-500" />
        {children || 'Light'}
      </span>
    )
  }
  if (j === 'dark') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 bg-slate-800 px-2.5 py-0.5 text-sm font-medium text-white">
        <JerseyIcon className="h-3.5 w-3.5 text-slate-300" />
        {children || 'Dark'}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-ice-200 bg-ice-50 px-2.5 py-0.5 text-sm font-medium text-ice-700">
      <JerseyIcon className="h-3.5 w-3.5 text-ice-500" />
      {children || 'Either'}
    </span>
  )
}

const baseField =
  'w-full rounded-lg border-2 border-slate-200 bg-white px-4 py-3 text-base text-slate-900 placeholder-slate-400 focus:border-ice-500 focus:outline-none focus:ring-1 focus:ring-ice-500'

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      {label && (
        <span className="mb-1 block text-sm font-medium text-slate-600">
          {label}
        </span>
      )}
      {children}
      {hint && <span className="mt-1 block text-sm text-slate-500">{hint}</span>}
    </label>
  )
}

export function TextInput(props) {
  return <input {...props} className={`${baseField} ${props.className || ''}`} />
}

export function TextArea(props) {
  return (
    <textarea
      {...props}
      className={`${baseField} leading-relaxed ${props.className || ''}`}
    />
  )
}

export function Select(props) {
  const { children, ...rest } = props
  return (
    <select {...rest} className={`${baseField} ${rest.className || ''}`}>
      {children}
    </select>
  )
}

export function Button({ children, variant = 'primary', className = '', ...rest }) {
  const variants = {
    primary:
      'bg-ice-500 text-white hover:bg-ice-400 shadow-lg shadow-ice-500/25 disabled:bg-ice-300 disabled:shadow-none',
    secondary:
      'bg-white text-slate-700 border-2 border-slate-200 hover:border-ice-500 hover:text-ice-700 disabled:opacity-50',
    success:
      'bg-green-500 text-white hover:bg-green-400 disabled:bg-green-300',
    danger: 'bg-rose-500 text-white hover:bg-rose-400 disabled:bg-rose-300',
    ghost:
      'bg-transparent text-slate-600 border-2 border-slate-200 hover:bg-slate-50 hover:text-slate-900',
  }
  return (
    <button
      {...rest}
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-base font-semibold transition disabled:cursor-not-allowed ${
        variants[variant] || variants.primary
      } ${className}`}
    >
      {children}
    </button>
  )
}

// ---- Lightweight inline SVG icons -----------------------------------------

export function PuckIcon({ className = 'h-5 w-5' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <ellipse cx="12" cy="8" rx="8" ry="3.2" opacity="0.55" />
      <path d="M4 8v8c0 1.77 3.58 3.2 8 3.2s8-1.43 8-3.2V8c0 1.77-3.58 3.2-8 3.2S4 9.77 4 8z" />
    </svg>
  )
}

// Goalie mask glyph.
export function MaskIcon({ className = 'h-4 w-4' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M12 2c-4.4 0-7 3.2-7 8 0 5 3 12 7 12s7-7 7-12c0-4.8-2.6-8-7-8zm-3 7.5a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4zm6 0a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4zM10 15h4l-.7 2.5h-2.6L10 15z" />
    </svg>
  )
}

// Simple jersey/sweater silhouette.
export function JerseyIcon({ className = 'h-4 w-4' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M8 3L4 6l2 3 1.5-1V21h9V8L18 9l2-3-4-3-2 1.6c-.6.5-1.4.5-2 0L8 3z" />
    </svg>
  )
}

export function ShieldIcon({ className = 'h-6 w-6' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M12 2l8 3v6c0 5-3.4 9.3-8 11-4.6-1.7-8-6-8-11V5l8-3z" />
    </svg>
  )
}
